import React, { useEffect, useState } from 'react';
import { Modal, Checkbox, Radio, Empty, Spin, Alert, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import {
  GET_STATION_ASSOCIATED_ADDRESSES,
  GetStationAssociatedAddressesResponse,
  GetStationAssociatedAddressesVariables,
  StationAssociatedAddress,
} from '../graphql/queries/getStationAssociatedAddresses';
import { SiteAddressForSave } from './PickupSiteFormDrawer';
import { MODAL_HEADER_STYLE, MODAL_FOOTER_STYLE, modalBodyStyle, modalTitle } from '../utils/modalChrome';

export interface SplitSelection {
  /** 拆出的其他关联地址 relate id（改属新站点）。 */
  movedRelateIds: number[];
  /** 主揽收地址在原站点的旧 relate id（逻辑删）。 */
  primaryOldRelateId: number;
  /** 主揽收地址（预填新站点揽收地址）。 */
  primaryAddress: SiteAddressForSave;
}

interface Props {
  open: boolean;
  cooperationWarehouseId?: number;
  onClose: () => void;
  /** 确认拆分：进入新建站点（带入主地址 + 其余选中关联地址迁移信息）。 */
  onConfirm: (selection: SplitSelection) => void;
}

/**
 * 拆分站点弹窗（#5）：展示站点的合并关联地址（address_source_type=2 启用），多选拆出 + 选主揽收地址。
 * 确认后由上层进入新建站点表单（带入原站点基础信息 + 主揽收地址），保存后调 resolveStationSplit 迁移 relate。
 */
const SplitStationModal: React.FC<Props> = ({ open, cooperationWarehouseId, onClose, onConfirm }) => {
  const { t } = useTranslation();
  const sp = (k: string, d?: string) => t(`pickup.addressReviewPool.split.${k}`, d ?? '') as string;
  const [checked, setChecked] = useState<number[]>([]);
  const [primaryId, setPrimaryId] = useState<number | undefined>(undefined);

  const { data, loading } = useQuery<GetStationAssociatedAddressesResponse, GetStationAssociatedAddressesVariables>(
    GET_STATION_ASSOCIATED_ADDRESSES,
    { variables: { cooperationWarehouseId: cooperationWarehouseId as number }, skip: !open || !cooperationWarehouseId, fetchPolicy: 'network-only' },
  );

  useEffect(() => {
    if (open) {
      setChecked([]);
      setPrimaryId(undefined);
    }
  }, [open]);

  const rows: StationAssociatedAddress[] = data?.getStationAssociatedAddresses ?? [];
  const addrText = (r: StationAssociatedAddress) =>
    [r.addressLine1, r.addressLine2, r.city, r.state, r.zipcode].filter((x) => x && `${x}`.trim()).join(', ');

  const handleConfirm = () => {
    if (!checked.length || primaryId == null) return;
    const primary = rows.find((r) => r.relateId === primaryId);
    if (!primary) return;
    onConfirm({
      movedRelateIds: checked.filter((id) => id !== primaryId),
      primaryOldRelateId: primaryId,
      primaryAddress: {
        addressLine1: primary.addressLine1 ?? '',
        addressLine2: primary.addressLine2 ?? '',
        city: primary.city ?? '',
        state: primary.state ?? '',
        zipcode: primary.zipcode ?? '',
        country: 'US',
      },
    });
  };

  // 主揽收地址必须在选中集合内
  const primaryValid = primaryId != null && checked.includes(primaryId);

  return (
    <Modal
      title={modalTitle(sp('title', '拆分站点'))}
      open={open}
      width={640}
      onCancel={onClose}
      okText={sp('confirm', '拆分站点')}
      cancelText={t('common.cancel', '取消') as string}
      okButtonProps={{ disabled: !checked.length || !primaryValid }}
      onOk={handleConfirm}
      // header/footer 固定并各带分隔线，滚动只发生在两线之间（对齐 PRD 原型）
      styles={{
        header: MODAL_HEADER_STYLE,
        body: modalBodyStyle('60vh'),
        footer: MODAL_FOOTER_STYLE,
      }}
    >
      <Alert type="info" showIcon className="mb-3" message={sp('hint', '选择要拆分到新站点的关联地址，并指定其中一个为新站点的揽收地址')} />
      {loading ? (
        <div className="flex justify-center py-8"><Spin /></div>
      ) : rows.length === 0 ? (
        <Empty description={sp('empty', '该站点暂无可拆分的关联地址')} />
      ) : (
        <div className="max-h-96 space-y-2 overflow-auto pr-1">
          <Checkbox.Group value={checked} onChange={(v) => setChecked(v as number[])} className="w-full">
            <Radio.Group value={primaryId} onChange={(e) => setPrimaryId(e.target.value)} className="w-full">
              {rows.map((r) => {
                const isChecked = checked.includes(r.relateId);
                return (
                  <div key={r.relateId} className="mb-2 rounded-lg border border-gray-200 p-2">
                    <div className="flex items-center gap-2">
                      <Checkbox value={r.relateId} />
                      <span className="flex-1 text-sm text-gray-800">{addrText(r) || '-'}</span>
                      <Radio value={r.relateId} disabled={!isChecked}>
                        {sp('asPrimary', '设为揽收地址')}
                      </Radio>
                    </div>
                  </div>
                );
              })}
            </Radio.Group>
          </Checkbox.Group>
          {checked.length > 0 && !primaryValid && (
            <Tag color="orange">{sp('pickPrimary', '请在已选地址中指定一个揽收地址')}</Tag>
          )}
        </div>
      )}
    </Modal>
  );
};

export default SplitStationModal;
